import { API } from '@/lib/api';
import { useState } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Star, Send, DollarSign, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";



const TIP_PRESETS = [2, 5, 10, 15];

const ReviewModal = ({ requestId, revieweeName, revieweeRole, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleTipPreset = (amount) => {
    setTipAmount(amount);
    setCustomTip("");
  };

  const handleCustomTip = (val) => {
    const num = parseFloat(val);
    setCustomTip(val);
    setTipAmount(isNaN(num) || num < 0 ? 0 : num);
  };

  const handleSubmit = async () => {
    if (rating === 0) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        request_id: requestId,
        rating,
        comment,
        tip_amount: tipAmount > 0 ? tipAmount : null,
      }, { withCredentials: true });
      toast.success("Review submitted! Thank you.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to submit review"));
    }
    setSubmitting(false);
  };

  const ratingLabels = ["", "Poor", "Fair", "Good", "Great", "Excellent"];
  const showTip = revieweeRole === "professional";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="review-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-emerald-950 mb-1" data-testid="review-title">Rate Your Experience</h3>
        <p className="text-sm text-stone-500 mb-1">
          {revieweeName ? `How was your experience with ${revieweeName}?` : "How was the service?"}
        </p>
        <p className="text-xs text-stone-400 flex items-center gap-1 mb-4">
          <Clock className="w-3 h-3" /> Review available for 24 hours
        </p>

        {/* Stars */}
        <div className="flex gap-2 mb-1 justify-center" data-testid="star-rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="transition-transform hover:scale-110"
              data-testid={`star-${star}`}
            >
              <Star
                className={`w-10 h-10 ${(hoverRating || rating) >= star ? "fill-amber-400 text-amber-400" : "text-stone-300"}`}
              />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-sm text-amber-600 font-medium mb-3" data-testid="rating-label">
            {ratingLabels[rating]}
          </p>
        )}

        <Textarea
          placeholder="Add a comment (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          className="mb-4"
          data-testid="review-comment"
        />

        {/* Tip Section (only for customer reviewing professional) */}
        {showTip && (
          <div className="border border-stone-200 rounded-xl p-4 mb-4" data-testid="tip-section">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-950">Add a Tip</span>
              <span className="text-xs text-stone-400 ml-auto">Optional</span>
            </div>
            <div className="flex gap-2 mb-3">
              {TIP_PRESETS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handleTipPreset(amount)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all border ${
                    tipAmount === amount && !customTip
                      ? "bg-emerald-950 text-white border-emerald-950"
                      : "bg-white text-stone-700 border-stone-200 hover:border-emerald-400"
                  }`}
                  data-testid={`tip-${amount}`}
                >
                  ${amount}
                </button>
              ))}
            </div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="number"
                min="0"
                step="0.50"
                placeholder="Custom amount"
                value={customTip}
                onChange={(e) => handleCustomTip(e.target.value)}
                className="pl-8"
                data-testid="custom-tip-input"
              />
            </div>
            {tipAmount > 0 && (
              <p className="text-xs text-emerald-600 mt-2 font-medium" data-testid="tip-amount-display">
                Tip: ${tipAmount.toFixed(2)} CAD
              </p>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="skip-review-btn">Skip</Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-review-btn"
          >
            <Send className="w-4 h-4 mr-1" /> {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
